import type { StepType } from "@reactour/tour";
import { create } from "zustand";
import { persist } from "zustand/middleware";

export interface TourItemRecord {
  completedAt: string;
  version: number;
}

interface TourStoreState {
  // Persisted state
  completedTours: Record<string, TourItemRecord>;

  // Ephemeral active tour runtime state
  activeTourId: string | null;
  activeSteps: StepType[];
  currentStep: number;
  isOpen: boolean;
  activeVersion: number;

  // Actions
  startTour: (
    tourId: string,
    steps: StepType[],
    options?: { version?: number; force?: boolean; startAt?: number },
  ) => boolean;
  closeTour: () => void;
  completeTour: (tourId?: string, version?: number) => void;
  setCurrentStep: (step: number | ((prev: number) => number)) => void;
  nextStep: () => void;
  prevStep: () => void;
  isTourCompleted: (tourId: string, minVersion?: number) => boolean;
  resetTour: (tourId: string) => void;
}

export const useTourStore = create<TourStoreState>()(
  persist(
    (set, get) => ({
      completedTours: {},

      activeTourId: null,
      activeSteps: [],
      currentStep: 0,
      isOpen: false,
      activeVersion: 1,

      isTourCompleted: (tourId: string, minVersion = 1) => {
        const record = get().completedTours[tourId];
        if (!record) return false;
        return record.version >= minVersion;
      },

      startTour: (tourId, steps, options) => {
        const force = options?.force ?? false;
        const version = options?.version ?? 1;

        if (!force && get().isTourCompleted(tourId, version)) {
          return false;
        }

        set({
          activeTourId: tourId,
          activeSteps: steps,
          currentStep: options?.startAt ?? 0,
          isOpen: true,
          activeVersion: version,
        });
        return true;
      },

      closeTour: () => {
        set({
          isOpen: false,
        });
      },

      completeTour: (tourId, version) => {
        const id = tourId ?? get().activeTourId;
        if (!id) return;
        const ver = version ?? get().activeVersion ?? 1;

        set((state) => ({
          isOpen: false,
          activeTourId: null,
          activeSteps: [],
          currentStep: 0,
          completedTours: {
            ...state.completedTours,
            [id]: {
              completedAt: new Date().toISOString(),
              version: ver,
            },
          },
        }));
      },

      setCurrentStep: (step) => {
        set((state) => ({
          currentStep:
            typeof step === "function" ? step(state.currentStep) : step,
        }));
      },

      nextStep: () => {
        const { currentStep, activeSteps, activeTourId, activeVersion } = get();
        if (currentStep < activeSteps.length - 1) {
          set({ currentStep: currentStep + 1 });
        } else {
          // Last step reached, complete tour
          if (activeTourId) {
            get().completeTour(activeTourId, activeVersion);
          } else {
            get().closeTour();
          }
        }
      },

      prevStep: () => {
        const { currentStep } = get();
        if (currentStep > 0) {
          set({ currentStep: currentStep - 1 });
        }
      },

      resetTour: (tourId) => {
        set((state) => {
          const updated = { ...state.completedTours };
          delete updated[tourId];
          return { completedTours: updated };
        });
      },
    }),
    {
      name: "thecoach_tour_store",
      partialize: (state) => ({
        completedTours: state.completedTours,
      }),
    },
  ),
);
